import React from "react";
import { useSelector, useDispatch, shallowEqual, useStore } from "react-redux";
import { reduce, pick } from "../utils/lodash";
import { StoreState } from "../models/states/StoreState";
import { MentionBuilderDelegate } from "../models/builders/MentionBuilder";
import { MentionModel } from "../models/MentionModel";
import { setVariableMentions } from "../redux/actions";
import { makeMentionsSelector } from "../redux/selectors/mentions";

export function useMentions(
    variableName: string,
): [MentionModel[], (builder: MentionBuilderDelegate) => void] {
    const dispatch = useDispatch();
    const mentionsSelector = makeMentionsSelector(variableName);
    const mentions = useSelector((state: StoreState) => mentionsSelector(state));

    const setMentions = React.useCallback(
        (builder: MentionBuilderDelegate) => {
            dispatch(setVariableMentions(variableName, builder));
        },
        [dispatch, variableName],
    );

    return [mentions, setMentions];
}

export function useMentionValues(
    variableName: string,
): [string[], (builder: MentionBuilderDelegate) => void] {
    const [mentions, setMentions] = useMentions(variableName);
    const mentionValues = React.useMemo(() => mentions.map((m) => m.value), [mentions]);
    return [mentionValues, setMentions];
}

export function useSetVariableMentions() {
    const dispatch = useDispatch();
    const matrix = useSelector(
        (state: StoreState) => state.interview.navigation.currentPage.loopIndex,
    );
    return (variableName: string, builder: MentionBuilderDelegate) =>
        dispatch(setVariableMentions(variableName, builder, matrix));
}

export function useGetVariableMentions() {
    const store = useStore<StoreState>();
    return (variableName: string) => makeMentionsSelector(variableName)(store.getState());
}

export function usePickMentions(
    variableNames: string[],
): { [variableName: string]: MentionModel[] } {
    return useSelector(
        (state: StoreState) =>
            reduce(
                pick(state.interview.variables.mentionsByVariableName, variableNames),
                (result, mentions, variableName) => ({
                    ...result,
                    [variableName]: mentions.filter(
                        (m) => m.matrix === state.interview.navigation.currentPage.loopIndex,
                    ),
                }),
                {},
            ),
        shallowEqual,
    );
}
